'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  FileText,
  DollarSign,
  Calendar,
  MapPin,
  Briefcase,
  Send,
  Edit,
  Download,
  CheckCircle,
  Clock,
  AlertTriangle,
  Building,
  User,
  Award,
  Gift,
  PiggyBank,
  Loader
} from 'lucide-react'
import { apiClient, handleApiError } from '@/lib/api'

interface OfferData {
  uuid?: string
  offer_status: string
  salary_offered: number
  salary_currency: string
  bonus_offered?: number
  equity_offered?: string
  benefits_package?: any
  start_date?: string
  offer_expiry_date?: string
  job_title: string
  job_level?: string
  department?: string
  location?: string
  work_type: string
  probation_period_months: number
  notice_period_days: number
  additional_terms?: string
  internal_notes?: string
  created_at?: string
  sent_at?: string
  candidate_first_name?: string
  candidate_last_name?: string
  candidate_email?: string
  position_title?: string
}

interface OfferLetterComponentProps {
  candidatePositionUuid: string
  candidateData?: any
}

export default function OfferLetterComponent({ candidatePositionUuid, candidateData }: OfferLetterComponentProps) {
  const [offer, setOffer] = useState<OfferData | null>(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'details' | 'timeline'>('details')
  const [timeline, setTimeline] = useState<any[]>([])
  const [loadingTimeline, setLoadingTimeline] = useState(false)

  // Form state for creating offer
  const [formData, setFormData] = useState({
    salary_offered: 100000,
    salary_currency: 'USD',
    bonus_offered: 0,
    equity_offered: '',
    job_title: candidateData?.position_title || 'Software Engineer',
    job_level: 'Mid',
    department: 'Engineering',
    location: candidateData?.position_location || 'Bangalore, India',
    work_type: 'hybrid',
    probation_period_months: 3,
    notice_period_days: 30,
    start_date: '',
    offer_expiry_date: '',
    additional_terms: 'Performance review after 6 months. Annual increment based on performance.',
    internal_notes: '',
    benefits_package: {
      health_insurance: 'Full medical coverage',
      vacation_days: 25,
      remote_work: 'Hybrid - 3 days office, 2 days remote',
      professional_development: '₹50,000 annual learning budget',
      provident_fund: '12% EPF contribution',
      gratuity: 'As per company policy'
    }
  })

  // Form state for editing offer
  const [editFormData, setEditFormData] = useState({
    salary_offered: 0,
    salary_currency: 'USD',
    bonus_offered: 0,
    equity_offered: '',
    job_title: '',
    job_level: '',
    department: '',
    location: '',
    work_type: 'hybrid',
    probation_period_months: 3,
    notice_period_days: 30,
    start_date: '',
    offer_expiry_date: '',
    additional_terms: '',
    internal_notes: '',
    benefits_package: {}
  })

  useEffect(() => {
    loadOfferData()
    if (activeTab === 'timeline') {
      loadTimelineData()
    }
  }, [candidatePositionUuid, activeTab])

  const loadOfferData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.get(`offers/by-candidate-position/${candidatePositionUuid}`)
      
      if (response.success) {
        setOffer(response.data?.offer || null)
      }
    } catch (error) {
      console.error('Error loading offer data:', error)
      setError('Failed to load offer data')
    } finally {
      setLoading(false)
    }
  }

  const loadTimelineData = async () => {
    try {
      setLoadingTimeline(true)
      
      const response = await apiClient.get(`offers/timeline/${candidatePositionUuid}`)
      
      if (response.success) {
        setTimeline(response.data?.timeline || [])
      } else {
        console.warn('Timeline API returned unsuccessful response:', response)
        setTimeline([]) // Set empty timeline on failure
      }
    } catch (error) {
      console.error('Error loading timeline data:', error)
      setTimeline([]) // Set empty timeline on error
      // Don't set main error for timeline, just log it
    } finally {
      setLoadingTimeline(false)
    }
  }

  const handleCreateOffer = async () => {
    try {
      setCreating(true)
      setError(null)

      // Set default dates if not provided
      const startDate = formData.start_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 30 days from now
      const expiryDate = formData.offer_expiry_date || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString() // 14 days from now

      const offerData = {
        candidate_position_uuid: candidatePositionUuid,
        ...formData,
        start_date: startDate,
        offer_expiry_date: expiryDate
      }

      const response = await apiClient.post('offers/create', offerData)

      if (response.success) {
        setOffer(response.data?.offer)
        setShowCreateForm(false)
        console.log('✅ Offer created successfully:', response.data?.offer)
      } else {
        throw new Error(response.message || 'Failed to create offer')
      }
    } catch (error) {
      console.error('Error creating offer:', error)
      setError(handleApiError(error).message)
    } finally {
      setCreating(false)
    }
  }

  const handleSendOffer = async () => {
    if (!offer?.uuid) return

    try {
      setLoading(true)
      
      const response = await apiClient.post(`offers/${offer.uuid}/send`)

      if (response.success) {
        setOffer(response.data?.offer)
        console.log('✅ Offer sent successfully')
      }
    } catch (error) {
      console.error('Error sending offer:', error)
      setError('Failed to send offer')
    } finally {
      setLoading(false)
    }
  }

  const handleEditOffer = () => {
    if (!offer) return
    
    // Populate edit form with current offer data
    setEditFormData({
      salary_offered: offer.salary_offered,
      salary_currency: offer.salary_currency,
      bonus_offered: offer.bonus_offered || 0,
      equity_offered: offer.equity_offered || '',
      job_title: offer.job_title,
      job_level: offer.job_level || '',
      department: offer.department || '',
      location: offer.location || '',
      work_type: offer.work_type,
      probation_period_months: offer.probation_period_months,
      notice_period_days: offer.notice_period_days,
      start_date: offer.start_date || '',
      offer_expiry_date: offer.offer_expiry_date || '',
      additional_terms: offer.additional_terms || '',
      internal_notes: offer.internal_notes || '',
      benefits_package: offer.benefits_package || {}
    })
    setEditMode(true)
  }

  const handleUpdateOffer = async () => {
    if (!offer?.uuid) return

    try {
      setUpdating(true)
      setError(null)

      // Clean up data before sending (remove NaN values and handle date fields)
      const updateData = Object.entries(editFormData).reduce((acc, [key, value]) => {
        // Skip null and undefined values
        if (value === null || value === undefined) {
          return acc
        }
        
        // For date fields, convert empty strings to null
        if (['start_date', 'offer_expiry_date'].includes(key)) {
          if (value === '') {
            acc[key] = null
          } else {
            acc[key] = value
          }
          return acc
        }
        
        // For numeric fields, check for NaN and convert properly
        if (['salary_offered', 'bonus_offered', 'probation_period_months', 'notice_period_days'].includes(key)) {
          if (typeof value === 'string') {
            // Check for string 'NaN' or 'nan' or empty string
            if (value.toLowerCase() === 'nan' || value === 'NaN' || value === '') {
              // Don't include this field in the update
              return acc
            }
            const numValue = parseFloat(value)
            if (isNaN(numValue)) {
              // Don't include invalid numeric values
              return acc
            }
            acc[key] = numValue
          } else if (typeof value === 'number') {
            if (isNaN(value)) {
              // Don't include NaN values
              return acc
            }
            acc[key] = value
          }
          return acc
        }
        
        // For string fields, include empty strings as they might be intentional
        if (typeof value === 'string') {
          acc[key] = value
          return acc
        }
        
        // Include all other values (objects, booleans, etc.)
        acc[key] = value
        return acc
      }, {})
      
      console.log('🔍 Original form data:', editFormData)
      console.log('🔍 Cleaned update data:', updateData)
      console.log('🔍 Update data keys:', Object.keys(updateData))
      console.log('🔍 Update data empty?', Object.keys(updateData).length === 0)
      console.log('🔍 Update data JSON:', JSON.stringify(updateData, null, 2))
      
      if (Object.keys(updateData).length === 0) {
        throw new Error('No valid data to update')
      }
      
      console.log('🔍 Sending request to:', `offers/${offer.uuid}`)
      console.log('🔍 Request payload:', JSON.stringify(updateData))
      
      const response = await apiClient.put(`offers/${offer.uuid}`, updateData)

      if (response.success) {
        setOffer(response.data?.offer)
        setEditMode(false)
        // Reload timeline to show the update
        loadTimelineData()
        console.log('✅ Offer updated successfully:', response.data?.offer)
      } else {
        throw new Error(response.message || 'Failed to update offer')
      }
    } catch (error) {
      console.error('Error updating offer:', error)
      setError(handleApiError(error).message)
    } finally {
      setUpdating(false)
    }
  }

  const handleCancelEdit = () => {
    setEditMode(false)
    setEditFormData({
      salary_offered: 0,
      salary_currency: 'USD',
      bonus_offered: 0,
      equity_offered: '',
      job_title: '',
      job_level: '',
      department: '',
      location: '',
      work_type: 'hybrid',
      probation_period_months: 3,
      notice_period_days: 30,
      start_date: '',
      offer_expiry_date: '',
      additional_terms: '',
      internal_notes: '',
      benefits_package: {}
    })
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    if (currency === 'USD') {
      return `$${amount.toLocaleString()}`
    } else if (currency === 'INR') {
      return `₹${amount.toLocaleString()}`
    }
    return `${currency} ${amount.toLocaleString()}`
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'text-gray-600 bg-gray-100'
      case 'sent': return 'text-blue-600 bg-blue-100'
      case 'viewed': return 'text-yellow-600 bg-yellow-100'
      case 'accepted': return 'text-green-600 bg-green-100'
      case 'rejected': return 'text-red-600 bg-red-100'
      case 'negotiating': return 'text-purple-600 bg-purple-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <Edit className="w-4 h-4" />
      case 'sent': return <Send className="w-4 h-4" />
      case 'viewed': return <Clock className="w-4 h-4" />
      case 'accepted': return <CheckCircle className="w-4 h-4" />
      case 'rejected': return <AlertTriangle className="w-4 h-4" />
      case 'negotiating': return <FileText className="w-4 h-4" />
      default: return <FileText className="w-4 h-4" />
    }
  }

  if (loading && !offer) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading offer details...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <h4 className="font-medium text-red-800">Error</h4>
        </div>
        <p className="text-red-700">{error}</p>
        <button 
          onClick={loadOfferData}
          className="mt-3 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    )
  }

  if (!offer) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Offer Letter</h3>
        </div>

        {!showCreateForm ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FileText className="w-5 h-5 text-blue-600" />
              <h4 className="font-medium text-blue-800">Create Offer Letter</h4>
            </div>
            <p className="text-blue-700 mb-4">
              No offer letter has been created for this candidate yet. Create a personalized offer based on their performance and qualifications.
            </p>
            <div className="flex space-x-3">
              <button 
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Offer Letter
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">Create New Offer</h4>
              <button 
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                <input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Level</label>
                <select
                  value={formData.job_level}
                  onChange={(e) => setFormData({ ...formData, job_level: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Junior">Junior</option>
                  <option value="Mid">Mid Level</option>
                  <option value="Senior">Senior</option>
                  <option value="Lead">Lead</option>
                  <option value="Principal">Principal</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Annual Salary</label>
                <div className="flex space-x-2">
                  <select
                    value={formData.salary_currency}
                    onChange={(e) => setFormData({ ...formData, salary_currency: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="INR">INR</option>
                  </select>
                  <input
                    type="number"
                    value={formData.salary_offered}
                    onChange={(e) => setFormData({ ...formData, salary_offered: parseInt(e.target.value) })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Signing Bonus</label>
                <input
                  type="number"
                  value={formData.bonus_offered}
                  onChange={(e) => setFormData({ ...formData, bonus_offered: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Work Type</label>
                <select
                  value={formData.work_type}
                  onChange={(e) => setFormData({ ...formData, work_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="onsite">On-site</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Terms</label>
              <textarea
                value={formData.additional_terms}
                onChange={(e) => setFormData({ ...formData, additional_terms: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleCreateOffer}
                disabled={creating}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {creating && <Loader className="w-4 h-4 animate-spin" />}
                <span>{creating ? 'Creating...' : 'Create Offer'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render existing offer
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Offer Letter</h3>
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(offer.offer_status)}`}>
          {getStatusIcon(offer.offer_status)}
          <span className="capitalize">{offer.offer_status}</span>
        </div>
      </div>

      {/* Edit Form Modal */}
      {editMode && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCancelEdit}></div>
            
            <div className="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium text-gray-900">Edit Offer Letter</h4>
                  <button 
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                    <input
                      type="text"
                      value={editFormData.job_title}
                      onChange={(e) => setEditFormData({ ...editFormData, job_title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Job Level</label>
                    <select
                      value={editFormData.job_level}
                      onChange={(e) => setEditFormData({ ...editFormData, job_level: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Junior">Junior</option>
                      <option value="Mid">Mid Level</option>
                      <option value="Senior">Senior</option>
                      <option value="Lead">Lead</option>
                      <option value="Principal">Principal</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Annual Salary</label>
                    <div className="flex space-x-2">
                      <select
                        value={editFormData.salary_currency}
                        onChange={(e) => setEditFormData({ ...editFormData, salary_currency: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="USD">USD</option>
                        <option value="INR">INR</option>
                      </select>
                      <input
                        type="number"
                        value={editFormData.salary_offered}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value)
                          setEditFormData({ 
                            ...editFormData, 
                            salary_offered: isNaN(value) ? 0 : value 
                          })
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Signing Bonus</label>
                    <input
                      type="number"
                      value={editFormData.bonus_offered}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value)
                        setEditFormData({ 
                          ...editFormData, 
                          bonus_offered: isNaN(value) ? 0 : value 
                        })
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                    <input
                      type="text"
                      value={editFormData.location}
                      onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Type</label>
                    <select
                      value={editFormData.work_type}
                      onChange={(e) => setEditFormData({ ...editFormData, work_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="onsite">On-site</option>
                      <option value="remote">Remote</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Terms</label>
                  <textarea
                    value={editFormData.additional_terms}
                    onChange={(e) => setEditFormData({ ...editFormData, additional_terms: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button 
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleUpdateOffer}
                    disabled={updating}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    {updating && <Loader className="w-4 h-4 animate-spin" />}
                    <span>{updating ? 'Updating...' : 'Update Offer'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Offer Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-white/20 p-2 rounded-lg">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-xl font-semibold">{offer.job_title}</h4>
              <p className="text-blue-100">
                {offer.candidate_first_name} {offer.candidate_last_name}
              </p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'details'
                  ? 'bg-white/20 text-white'
                  : 'text-blue-100 hover:text-white hover:bg-white/10'
              }`}
            >
              Offer Details
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'timeline'
                  ? 'bg-white/20 text-white'
                  : 'text-blue-100 hover:text-white hover:bg-white/10'
              }`}
            >
              Timeline
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'details' ? (
            <div className="space-y-6">
          {/* Compensation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <h5 className="font-medium text-green-800">Annual Salary</h5>
              </div>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(offer.salary_offered, offer.salary_currency)}
              </p>
            </div>

            {offer.bonus_offered && offer.bonus_offered > 0 && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Gift className="w-5 h-5 text-yellow-600" />
                  <h5 className="font-medium text-yellow-800">Signing Bonus</h5>
                </div>
                <p className="text-xl font-bold text-yellow-600">
                  {formatCurrency(offer.bonus_offered, offer.salary_currency)}
                </p>
              </div>
            )}

            {offer.equity_offered && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <PiggyBank className="w-5 h-5 text-purple-600" />
                  <h5 className="font-medium text-purple-800">Equity</h5>
                </div>
                <p className="text-lg font-bold text-purple-600">{offer.equity_offered}</p>
              </div>
            )}
          </div>

          {/* Job Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Building className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Department</p>
                  <p className="font-medium">{offer.department || 'Not specified'}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Award className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Level</p>
                  <p className="font-medium">{offer.job_level || 'Not specified'}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium">{offer.location || 'Not specified'}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Briefcase className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Work Type</p>
                  <p className="font-medium capitalize">{offer.work_type}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="font-medium">{offer.start_date || 'To be determined'}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Probation Period</p>
                  <p className="font-medium">{offer.probation_period_months} months</p>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          {offer.benefits_package && (
            <div>
              <h5 className="font-medium text-gray-900 mb-3">Benefits Package</h5>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(offer.benefits_package).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                      <span className="font-medium">{value as string}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Additional Terms */}
          {offer.additional_terms && (
            <div>
              <h5 className="font-medium text-gray-900 mb-3">Additional Terms</h5>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">{offer.additional_terms}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            {offer.offer_status === 'draft' && (
              <>
                <button 
                  onClick={handleEditOffer}
                  disabled={loading || editMode}
                  className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit Offer</span>
                </button>
                <button 
                  onClick={handleSendOffer}
                  disabled={loading || editMode}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading && <Loader className="w-4 h-4 animate-spin" />}
                  <Send className="w-4 h-4" />
                  <span>{loading ? 'Sending...' : 'Send Offer'}</span>
                </button>
              </>
            )}
            
            <button className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Download PDF</span>
            </button>
          </div>
            </div>
          ) : (
            /* Timeline View */
            <div className="space-y-4">
              <h5 className="font-medium text-gray-900 mb-4">Offer Timeline</h5>
              
              {loadingTimeline ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading timeline...</span>
                </div>
              ) : timeline.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No timeline events yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {timeline.map((event, index) => (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        event.event_type === 'offer_created' ? 'bg-blue-100 text-blue-600' :
                        event.event_type === 'offer_revised' ? 'bg-orange-100 text-orange-600' :
                        event.event_type === 'offer_sent' ? 'bg-green-100 text-green-600' :
                        event.event_type === 'offer_accepted' ? 'bg-emerald-100 text-emerald-600' :
                        event.event_type === 'offer_rejected' ? 'bg-red-100 text-red-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {event.event_type === 'offer_created' && <FileText className="w-4 h-4" />}
                        {event.event_type === 'offer_revised' && <Edit className="w-4 h-4" />}
                        {event.event_type === 'offer_sent' && <Send className="w-4 h-4" />}
                        {event.event_type === 'offer_accepted' && <CheckCircle className="w-4 h-4" />}
                        {event.event_type === 'offer_rejected' && <AlertTriangle className="w-4 h-4" />}
                        {!['offer_created', 'offer_revised', 'offer_sent', 'offer_accepted', 'offer_rejected'].includes(event.event_type) && <Clock className="w-4 h-4" />}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900">{event.description}</p>
                          <span className="text-sm text-gray-500">
                            {new Date(event.timestamp).toLocaleDateString()} at{' '}
                            {new Date(event.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        
                        {event.details && (
                          <div className="mt-2 space-y-1">
                            {event.details.salary_offered && (
                              <p className="text-sm text-gray-600">
                                💰 Salary: {event.details.salary_currency} {event.details.salary_offered?.toLocaleString()}
                              </p>
                            )}
                            {event.details.job_title && (
                              <p className="text-sm text-gray-600">📋 Role: {event.details.job_title}</p>
                            )}
                            {event.details.version && (
                              <p className="text-sm text-gray-600">🔄 Version: {event.details.version}</p>
                            )}
                            {event.details.changes && Array.isArray(event.details.changes) && (
                              <div className="text-sm text-gray-600">
                                <p>📝 Changes:</p>
                                <ul className="list-disc list-inside ml-4 space-y-1">
                                  {event.details.changes.map((change, idx) => (
                                    <li key={idx}>{change}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}